import autocast from 'autocast';
import dot from 'dot-object';
import PropTypes from 'prop-types';
import React, { Suspense, createRef } from 'react';

import i18n from '../../i18n';
import FiltersModel from '../../models/Filters';
import Group from '../../models/Group';
import User from '../../models/User';
import Utils from '../../utils/utils';

import Alert from './Alert';
import { isMapAreaFilters } from '../../utils/mapAreaFilters';

import Screen from '../../utils/Screen';
import SortOrderSheet from './filters/SortOrderSheet';

import { IconButton, Button, Flex, Icon } from '@homeexchange/design';
import { FiltersModal } from '@homeexchange/search';
import { Button as PanoramaButton, IconButton as PanoramaIconButton } from '@homeexchange/panorama';
import Tour from '../../utils/Tour';
import InputSearch from './InputSearch';

import {
    fromFiltersModalState,
    toFiltersModalGroups,
    toFiltersModalReverseHomes,
    toFiltersModalState
} from './filtersModalAdapter';
import { getActiveFiltersList } from './utils';
import { trackSearchResults, searchResultsData } from '../../utils/utils-ts';
import Transfert from '../../models/Transfert';

const ActiveFilters = React.lazy(() => import('./ActiveFilters'));

if ('moment' in window) {
    moment.locale(document.documentElement.lang);
}

const EXCLUDED_FILTERS = ['availability', 'home', 'placeId', 'sort', 'reciprocal', 'lastMinute'];
const EXCLUDED_ACTIVE_FILTERS = ['availability', 'bounds', 'home', 'placeId', 'sort', 'duration'];

const BAR_HIDE_DELTA = 60;
const BAR_SLIDE_DURATION = 150;

/* eslint-disable react/jsx-no-bind */
export class Filters extends React.PureComponent {
    static propTypes = {
        user: PropTypes.instanceOf(User),
        groups: PropTypes.arrayOf(PropTypes.instanceOf(Group)),
        homeFavorites: PropTypes.arrayOf(PropTypes.number),
        filters: PropTypes.object,
        resultsBounds: PropTypes.object,
        updateFilters: PropTypes.func,
        amenities: PropTypes.array,
        surrounding: PropTypes.array,
        gpRange: PropTypes.object,
        query: PropTypes.string,
        resultsCount: PropTypes.number,
        toggleMoreFiltersModalDisplay: PropTypes.func,
        displayAlerts: PropTypes.bool,
        onRemoveFilter: PropTypes.func,
        displayMap: PropTypes.bool,
        openMoreFiltersModal: PropTypes.bool,
        shouldShowAdditionalResult: PropTypes.bool
    };

    static defaultProps = {
        groups: [],
        shouldShowAdditionalResult: false
    };

    constructor(props) {
        super(props);
        const initialFilters = this.stateFiltersFromProps(props);
        this.state = {
            filters: initialFilters,
            committedFilters: initialFilters,
            calendar: props.filters.calendar,
            originalBoundsFilters: this.props.filters,
            openGuestNb: false,
            height: 0,
            width: 0,
            screenState: {
                isMobile: Screen.isMobile(),
                noDesktop: Screen.noDesktop()
            },
            showCalendarModal: false,
            showBecomeMemberBanner: true,
            activeFiltersKey: 0
        };
        this.onFilterUpdateAndFetch = this.onFilterUpdateAndFetch.bind(this);
        this.onFilterObjectUpdate = this.onFilterObjectUpdate.bind(this);
        this.onExchangeTypeUpdateAndFetch = this.onExchangeTypeUpdateAndFetch.bind(this);
        this.submitFilters = this.submitFilters.bind(this);
        this.resetFilters = this.resetFilters.bind(this);
        this.updateFilters = this.updateFilters.bind(this);
        this.countFilters = this.countFilters.bind(this);
        this.hideTourForever = this.hideTourForever.bind(this);
        this.customSubmitFilters = this.customSubmitFilters.bind(this);
        this.openGuestNb = this.openGuestNb.bind(this);
        this.handleOpenGuestNb = this.handleOpenGuestNb.bind(this);
        this.updateGuestFilters = this.updateGuestFilters.bind(this);
        this.handleOnQuickFilterChange = this.handleOnQuickFilterChange.bind(this);
        this.updateMarginTopOfSearchActiveFilters = this.updateMarginTopOfSearchActiveFilters.bind(this);
        this.onResize = this.onResize.bind(this);
        this.setShowCalendarModal = this.setShowCalendarModal.bind(this);
        this.updateCalendar = this.updateCalendar.bind(this);
        this.refreshFiltersLayout = this.refreshFiltersLayout.bind(this);
        this.divRef = createRef();
        this.guestNb = createRef();
        this.popupGuestNb = createRef();
        this.radioHome = createRef();
        this.radioFlat = createRef();
        this.radioPrivate = createRef();
        this.extraFilters = createRef();
        this.handleResize = _.debounce(this.handleResize.bind(this), 200);
        this.handlePanelScroll = this.handlePanelScroll.bind(this);
        this.scrollPanel = null;
        this.lastScrollTop = 0;
        this.downwardDelta = 0;
        this.barPinned = false;
        this.barUnpinning = false;
        this.barFlowOffset = 0;
        this.unpinTimer = null;
        this.scrollRafPending = false;
        this.timeoutRefreshFiltersLayout = null;
        this.filtersModalInitialValue = null;
        this.delay = 100;
        this.renderFiltersModal = this.renderFiltersModal.bind(this);
        this.handleFiltersModalSubmit = this.handleFiltersModalSubmit.bind(this);
        this.handleFiltersModalReset = this.handleFiltersModalReset.bind(this);
        this.toggleOpenMoreFilters = this.toggleOpenMoreFilters.bind(this);
        this.reformatBooleanFilter = this.reformatBooleanFilter.bind(this);
        this.handleCloseBecomeMemberBanner = this.handleCloseBecomeMemberBanner.bind(this);
    }

    stateFiltersFromProps(props) {
        const filters = new FiltersModel(props.filters);
        return filters.flatify('Filters');
    }

    reformatBooleanFilter(filter, value) {
        if (!Array.isArray(value)) {
            return value;
        }

        switch (value.length) {
            case 1:
                if (filter === FiltersModel.IS_PRIMARY.name) {
                    return value[0] === FiltersModel.IS_PRIMARY.primary ? true : false;
                } else if (filter === FiltersModel.IS_PRIVATE_ROOM.name) {
                    return value[0] === FiltersModel.IS_PRIVATE_ROOM.private ? true : false;
                } else {
                    return value[0];
                }

            case 2:
            default:
                return null;
        }
    }

    filtersFromState() {
        const filters = Object.assign({}, this.state.filters);

        if ((filters.is_primary ?? null) !== null) {
            filters.is_primary = this.reformatBooleanFilter(FiltersModel.IS_PRIMARY.name, filters.is_primary);
        }

        if ((filters.home_type ?? null) !== null) {
            filters.home_type = this.reformatBooleanFilter(FiltersModel.HOME_TYPE.name, filters.home_type);
        }

        if ((filters.is_private_room ?? null) !== null) {
            filters.is_private_room = this.reformatBooleanFilter(
                FiltersModel.IS_PRIVATE_ROOM.name,
                filters.is_private_room
            );
        }

        const valideFilters = FiltersModel.fromFlatFilters(filters);
        valideFilters.set(
            'location',
            Object.assign({}, this.props.filters.location, valideFilters.get('location'))
        );
        valideFilters.set('calendar', Object.assign({}, this.state.calendar));
        return valideFilters.toJSON();
    }

    handleOnQuickFilterChange(filter, value) {
        if (!value) {
            this.props.onRemoveFilter(filter, value);
        } else {
            this.props.updateFilters({ ...this.filtersFromState(), [filter]: value });
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        let newOriginalBoundsFilters =
            nextProps.filters &&
            nextProps.filters.placeId &&
            this.state.originalBoundsFilters &&
            !_.isEmpty(this.state.originalBoundsFilters) &&
            this.state.originalBoundsFilters.placeId == nextProps.filters.placeId
                ? this.state.originalBoundsFilters
                : nextProps.filters;

        if (
            nextProps.filters &&
            nextProps.filters.placeId &&
            nextProps.filters.location &&
            nextProps.filters.location != this.state.originalBoundsFilters.location &&
            this.state.originalBoundsFilters.placeId == nextProps.filters.placeId
        ) {
            const prevLocation = this.state.originalBoundsFilters.location;
            newOriginalBoundsFilters = Object.assign({}, nextProps.filters, {
                location: prevLocation
            });
        }

        const newFilters = this.stateFiltersFromProps(nextProps);
        const newCommittedFilters = this.stateFiltersFromProps(nextProps);

        const filtersHomeChanged =
            JSON.stringify(nextProps.filters.home) !== JSON.stringify(this.props.filters.home);

        if (!filtersHomeChanged && this.state.filters?.home) {
            newFilters.home = this.state.filters.home;
        }

        this.setState({
            filters: newFilters,
            committedFilters: newCommittedFilters,
            calendar: nextProps.filters.calendar,
            originalBoundsFilters: newOriginalBoundsFilters
        });
    }

    componentDidUpdate(prevProps) {
        const { openMoreFiltersModal } = this.props;
        if (Screen.isMobile() && openMoreFiltersModal !== prevProps.openMoreFiltersModal) {
            this.updateMarginTopOfSearchActiveFilters();
            this.displaySmartBanner();
        }

        if (prevProps.displayMap === true && this.props.displayMap === false) {
            this.updateHeightSearchFilters(openMoreFiltersModal);
            this.updateMarginTopOfSearchActiveFilters();
        }

        if (
            prevProps.displayMap !== this.props.displayMap ||
            prevProps.openMoreFiltersModal !== openMoreFiltersModal
        ) {
            this.setBarPinned(false);
        }
    }

    displaySmartBanner() {
        const hasSmartbanner = $('html').hasClass('smartbanner-show');
        const isHidden = $('.smartbanner').hasClass('hide-smartbanner');

        if (isHidden) {
            $('.smartbanner').removeClass('hide-smartbanner');
            $('html').addClass('smartbanner-show');
        } else if (hasSmartbanner) {
            $('.smartbanner').addClass('hide-smartbanner');
            $('html').removeClass('smartbanner-show');
        }
    }

    componentDidMount() {
        document.addEventListener('click', this.handleOpenGuestNb);
        this.handleResize();
        this.searchFilters = document.querySelector('.search-filters');
        this.scrollPanel = this.searchFilters?.closest('.search-panel') ?? null;
        this.barFlowOffset = this.searchFilters?.offsetTop ?? 0;
        this.scrollPanel?.addEventListener('scroll', this.handlePanelScroll, { passive: true });
        window.addEventListener('resize', this.handleResize);
        this.removeResizeListener = Screen.addResizeListener((screenState) => {
            this.setState({ screenState });
        });
        this.debouncedRemoveListener = _.debounce(() => {
            this.removeResizeListener();
        }, 200);
        window.addEventListener('beforeunload', this.debouncedRemoveListener);
    }

    updateMarginTopOfSearchActiveFilters() {
        if (this.searchFilter) {
            this.setState({
                height: this.searchFilter.clientHeight
            });
        }
    }

    componentWillUnmount() {
        document.removeEventListener('click', this.handleOpenGuestNb);
        this.scrollPanel?.removeEventListener('scroll', this.handlePanelScroll);
        clearTimeout(this.unpinTimer);
        window.removeEventListener('resize', this.handleResize);
        window.removeEventListener('beforeunload', this.debouncedRemoveListener);
        if (this.timeoutRefreshFiltersLayout !== null) {
            clearTimeout(this.timeoutRefreshFiltersLayout);
        }
    }

    isBarVisibilityLocked() {
        return this.state.showCalendarModal || this.props.openMoreFiltersModal;
    }

    handlePanelScroll() {
        if (this.isBarVisibilityLocked()) {
            this.setBarPinned(false);
            return;
        }
        if (this.scrollRafPending) {
            return;
        }
        this.scrollRafPending = true;
        window.requestAnimationFrame(() => {
            this.scrollRafPending = false;
            this.updateBarVisibility();
        });
    }

    updateBarVisibility() {
        if (!this.scrollPanel || !this.searchFilters) {
            return;
        }
        const scrollTop = this.scrollPanel.scrollTop;
        const delta = scrollTop - this.lastScrollTop;
        this.lastScrollTop = scrollTop;
        this.downwardDelta = delta > 0 ? this.downwardDelta + delta : 0;

        if (scrollTop <= this.barFlowOffset) {
            this.setBarPinned(false);
        } else if (delta < 0 && scrollTop > this.barFlowOffset + this.searchFilters.offsetHeight) {
            this.setBarPinned(true);
        } else if (this.downwardDelta > BAR_HIDE_DELTA) {
            this.unpinBarWithSlideOut();
        }
    }

    unpinBarWithSlideOut() {
        if (!this.searchFilters || !this.barPinned) {
            return;
        }
        if (Screen.matchesMedia('(prefers-reduced-motion: reduce)')) {
            this.setBarPinned(false);
            return;
        }
        this.barPinned = false;
        this.barUnpinning = true;
        this.searchFilters.classList.add('search-filters-unpinning');
        this.unpinTimer = setTimeout(() => {
            this.barUnpinning = false;
            this.searchFilters.classList.remove('search-filters-pinned', 'search-filters-unpinning');
            this.barFlowOffset = this.searchFilters.offsetTop;
        }, BAR_SLIDE_DURATION);
    }

    setBarPinned(pinned) {
        if (!this.searchFilters || (this.barPinned === pinned && !this.barUnpinning)) {
            return;
        }
        clearTimeout(this.unpinTimer);
        if (pinned && this.barUnpinning) {
            this.searchFilters.classList.remove('search-filters-pinned', 'search-filters-unpinning');
            this.barFlowOffset = this.searchFilters.offsetTop;
        }
        this.barUnpinning = false;
        this.barPinned = pinned;
        if (pinned) {
            this.downwardDelta = 0;
        }
        this.searchFilters.classList.remove('search-filters-unpinning');
        this.searchFilters.classList.toggle('search-filters-pinned', pinned);
        if (!pinned) {
            this.barFlowOffset = this.searchFilters.offsetTop;
        }
    }

    updateHeightSearchFilters(openMoreFiltersModal = false) {
        if (!this.searchFilters) return;

        const searchActiveFilters = document.querySelector('.search-active-filters');

        if (Screen.noDesktop()) {
            const searchFilterHeight = this.searchFilter?.clientHeight;

            if (searchFilterHeight !== undefined) {
                const newHeight =
                    openMoreFiltersModal || !searchActiveFilters ? `${searchFilterHeight}px` : 'auto';
                this.setParentHeight(newHeight);
            }
        } else {
            this.setParentHeight('');
        }
    }

    setParentHeight(height) {
        this.searchFilters.style.height = height;
    }

    handleResize() {
        this.updateMarginTopOfSearchActiveFilters();
        this.updateHeightSearchFilters(this.props.openMoreFiltersModal);
        if (!this.barPinned && this.searchFilters) {
            this.barFlowOffset = this.searchFilters.offsetTop;
        }
    }

    onExchangeTypeUpdateAndFetch(event) {
        this.onExchangeTypeUpdateFromSelect(event, true);
    }

    onExchangeTypeUpdateFromSelect(event, fetch) {
        // eslint-disable-next-line camelcase
        const calendar = { ...this.state.calendar };

        if (!event.target.checked) {
            calendar.exchange_types = calendar.exchange_types.filter(
                (exchType) => exchType !== event.target.value
            );
        } else {
            const exchangeTypes = calendar.exchange_types ? [...calendar.exchange_types] : [];
            exchangeTypes.push(event.target.value);
            calendar.exchange_types = exchangeTypes;
        }

        const filtersCopy = JSON.parse(
            JSON.stringify({
                ...this.state.filters,
                calendar
            })
        );
        this.setState({
            filters: filtersCopy,
            calendar
        });
        this.updateFilters(filtersCopy, fetch);
    }

    onFilterUpdateAndFetch(event) {
        trackSearchResults(null, searchResultsData.searchParam.sortBy, event.target.value);
        this.updateFilterFormInput(event.target, true);
    }

    onFilterObjectUpdate(event) {
        this.updateFilterFormCheckboxObject(event.target);
    }

    isObjectChecked(object, value) {
        if (object && dot.pick(object, this.state.filters) == value) {
            return true;
        }
        return false;
    }

    updateFilterFormInput(input, fetch) {
        let filters = _.clone(this.state.filters);
        // reset filter
        if (input.type === 'checkbox' && !input.checked) {
            delete filters[input.name];
        } else if (input.id === 'exchange_type' && input.value === '') {
            delete filters[input.name];
        }
        // add/update filter
        else {
            const newFilter = {};
            if (input.name === 'home_type') {
                delete filters.home_type;
            }
            newFilter[input.name] = autocast(input.value);
            filters = Object.assign({}, filters, newFilter);
        }
        this.updateFilters(filters, fetch);
    }

    updateFilterFormCheckboxObject(input, fetch) {
        let values = dot.pick(input.name, this.state.filters);
        const newValues = {};

        if (input.checked) {
            values = autocast(input.value);
            dot.str(input.name, values, newValues);
        } else {
            values = null;
            dot.remove(input.name, this.state.filters);
        }
        const filters = Object.assign({}, this.state.filters, newValues);
        this.updateFilters(filters, fetch);
    }

    updateGuestFilters(adults, children, babies) {
        let { filters } = this.state;

        filters = {
            ...filters,
            home: {
                size: {
                    beds: {
                        adults: adults ? adults : null,
                        children: children ? children : null,
                        babies: babies ? babies : null
                    }
                }
            }
        };

        Utils.closeModalsFullScreen();

        if (Screen.isMobile()) {
            // Apply for iOS
            Utils.applyStyles('.layout-login.search-body', {
                paddingTop: '80px'
            });

            Utils.applyStyles(
                '.search.container-fluid',
                {},
                {
                    selector: '.row',
                    styles: { height: '' }
                }
            );
        }

        this.updateFilters(filters, true);
        this.setState({ openGuestNb: false });
    }

    openGuestNb(open) {
        const openGuestNb = Boolean(open);

        if (openGuestNb) {
            Utils.openModalsFullScreen();
        } else {
            Utils.closeModalsFullScreen();
        }

        if (Screen.isMobile()) {
            // Apply for iOS
            Utils.applyStyles('.layout-login.search-body', {
                paddingTop: openGuestNb ? 0 : '80px'
            });

            Utils.applyStyles(
                '.search.container-fluid',
                {},
                {
                    selector: '.row',
                    styles: { height: openGuestNb ? 'auto' : '' }
                }
            );
        }

        this.setState({
            openGuestNb,
            outSide: false
        });
    }

    handleOpenGuestNb(event) {
        event.stopPropagation();
        if (
            !this.guestNb.current.contains(event.target) &&
            this.state.openGuestNb &&
            !this.popupGuestNb.current.contains(event.target)
        ) {
            this.setState({
                ...(!Screen.isMobile() && { openGuestNb: false }),
                outSide: true
            });
        }
    }

    updateFilters(filters, fetch) {
        this.setState(
            {
                filters
            },
            () => {
                if (fetch) {
                    this.submitFilters();
                }
            }
        );
    }

    resetFilters(e, toggleModalDisplay = true) {
        e.preventDefault();
        const {
            calendar: { date_ranges: dateRanges, flexibility }
        } = this.props.filters;

        const filtersToAdd = {
            calendar: {
                ...(dateRanges && { date_ranges: [...dateRanges] }),
                flexibility
            }
        };

        this.setState(
            {
                filters: {
                    ...filtersToAdd,
                    home: { ...this.props.filters.home }
                },
                calendar: { ...filtersToAdd.calendar }
            },
            () => {
                if (toggleModalDisplay) {
                    this.props.toggleMoreFiltersModalDisplay();
                }
                // Do not submit if there was no active filters
                if (
                    getActiveFiltersList(this.stateFiltersFromProps(this.props), EXCLUDED_ACTIVE_FILTERS)
                        .length > 0
                ) {
                    this.submitFilters();
                }
            }
        );
    }

    submitFilters() {
        this.props.updateFilters(this.filtersFromState());
    }

    customSubmitFilters() {
        this.toggleOpenMoreFilters();
        this.submitFilters();
        this.setState((prevState) => ({ activeFiltersKey: prevState.activeFiltersKey + 1 }));
    }

    updateCalendar(calendar) {
        // format dates
        this.setState({
            calendar
        });
        const filtersCopy = JSON.parse(JSON.stringify(this.state.filters));
        this.updateFilters(filtersCopy, true);
    }

    hideTour() {
        Tour.hideIfStep(0);
    }

    hideTourForever() {
        Tour.hideForeverIfStep(1);
    }

    tourNextStep() {
        Tour.nextStepIfAtStep('search_tour', 0);
    }

    toggleOpenMoreFilters() {
        trackSearchResults(searchResultsData.text.moreFilters);

        const { toggleMoreFiltersModalDisplay, openMoreFiltersModal } = this.props;
        toggleMoreFiltersModalDisplay();
        this.updateHeightSearchFilters(!openMoreFiltersModal);
    }

    countFilters(excludedObj, filters = this.state.filters) {
        return Object.entries(filters).reduce((acc, [key, value]) => {
            let size;
            if (_.contains(excludedObj, key)) {
                size = 0;
            } else if (value && value.length && !['reverse', 'preferred_destination'].includes(key)) {
                if (key === 'home_type') {
                    size = value.length;
                } else {
                    size = value.filter((v) => !_.contains(excludedObj, v)).length;
                }
            } else if (value && !_.isEmpty(value)) {
                if (key === 'calendar') {
                    size = value.exchange_types?.length ?? 0;
                } else {
                    size = 1;
                }
            } else if (_.isBoolean(value) || _.isString(value) || _.isNumber(value)) {
                if ((key === 'lastMinute' || key === 'luxury') && !value) {
                    size = 0;
                } else {
                    size = 1;
                }
            } else {
                size = 0;
            }
            return acc + size;
        }, 0);
    }

    setShowCalendarModal(show) {
        this.setBarPinned(false);
        if (show) {
            Utils.openModalsFullScreen();
        } else {
            Utils.closeModalsFullScreen();
        }

        if (Screen.isMobile()) {
            // Apply for iOS
            Utils.applyStyles('.badge-more-filter', {
                display: show ? 'none' : ''
            });

            Utils.applyStyles('.react-resize-detector', {
                position: show ? 'fixed' : '',
                zIndex: show ? 10 : '',
                top: show ? 0 : '',
                bottom: show ? 0 : '',
                right: show ? 0 : '',
                left: show ? 0 : ''
            });

            // Apply for iOS when input search is feed
            Utils.applyStyles(
                '.search.container-fluid',
                {
                    position: show ? 'fixed' : '',
                    zIndex: show ? 10 : '',
                    top: show ? 0 : '',
                    bottom: show ? 0 : '',
                    right: show ? 0 : '',
                    left: show ? 0 : ''
                },
                {
                    selector: '.row',
                    styles: { height: show ? 'auto' : '' }
                }
            );

            Utils.applyStyles('.search-filters', {
                position: show ? 'fixed' : '',
                zIndex: show ? 10 : ''
            });

            Utils.applyStyles('.form.form-filters', {
                position: show ? 'fixed' : ''
            });

            Utils.applyStyles('.search-filter-container', {
                top: show ? 0 : ''
            });

            Utils.applyStyles('.container-search-filter-row', {
                position: show ? 'fixed' : '',
                zIndex: show ? 10 : '',
                top: show ? 0 : '',
                bottom: show ? 0 : '',
                right: show ? 0 : '',
                left: show ? 0 : ''
            });

            Utils.applyStyles('.search-filter-row', {
                width: show ? '100%' : '',
                overflow: show ? 'initial' : ''
            });
        }

        this.setState({ showCalendarModal: show });
    }

    reactResizeDetector(width, height, targetRef, calendarTree) {
        return (
            <div ref={targetRef} className="react-resize-detector">
                {this.props.user && (
                    <>
                        <div className="form-group date-range visible-xs visible-sm">
                            <label htmlFor="date_range" className="sr-only">
                                {i18n.t('search:filters.arrival')} {'->'} {i18n.t('search:filters.departure')}
                            </label>
                            <InputSearch
                                mode="range"
                                nbMonths={Screen.isMobile() ? 1 : 2}
                                classname={['btn btn-default date_range']}
                                calendar={calendarTree}
                                setCalendar={this.updateCalendar}
                                isFilter={true}
                                showModal={this.state.showCalendarModal}
                                setShowModal={this.setShowCalendarModal}
                                context={'filters'}
                            />
                        </div>
                    </>
                )}
            </div>
        );
    }

    onResize(width) {
        this.setState({ width });
    }

    refreshFiltersLayout() {
        this.timeoutRefreshFiltersLayout = setTimeout(() => {
            this.updateHeightSearchFilters();
            this.updateMarginTopOfSearchActiveFilters();
        }, this.delay);
    }

    handleBecomeMemberBannerClick(e) {
        e.preventDefault();
        trackSearchResults(searchResultsData.text.searchBanner);

        window.open(e.target.href, '_blank');
    }

    handleCloseBecomeMemberBanner() {
        this.setState({ showBecomeMemberBanner: false });
    }

    isMobileMapView() {
        return Boolean(this.props.displayMap) && Screen.noDesktop();
    }

    renderAlert() {
        return (
            <Alert
                user={this.props.user}
                groups={this.props.groups}
                resultsBounds={this.props.resultsBounds}
                trackingOrigin={
                    this.isMobileMapView()
                        ? searchResultsData.origin.mapToolbar
                        : searchResultsData.origin.topMenu
                }
                search={{
                    query: this.props.query,
                    filters: isMapAreaFilters(this.props.filters)
                        ? this.props.filters
                        : this.state.originalBoundsFilters
                }}
            />
        );
    }

    renderFiltersButton(nbFilters) {
        const isMobile = Screen.isMobile();
        const label = i18n.t('search:filters-button');
        const size = { hierarchy: 'secondary', size: 'medium' };

        return (
            <Flex gap={2} alignItems="center" onFocus={this.hideTourForever} onClick={this.hideTourForever}>
                {isMobile ? (
                    <PanoramaIconButton
                        icon="Filter"
                        label={label}
                        onClick={this.toggleOpenMoreFilters}
                        type="button"
                        {...(nbFilters > 0 ? { ...size, isActive: true, badge: nbFilters } : size)}
                    />
                ) : (
                    <PanoramaButton
                        icon="Filter"
                        onClick={this.toggleOpenMoreFilters}
                        type="button"
                        {...(nbFilters > 0 ? { ...size, isActive: true, count: nbFilters } : size)}
                    >
                        {label}
                    </PanoramaButton>
                )}
                {this.props.resultsCount > 0 && (
                    <SortOrderSheet
                        asMenu={!isMobile}
                        value={this.state.filters?.sort}
                        onChange={(input) => this.onFilterUpdateAndFetch({ target: input })}
                    />
                )}
                {this.props.displayAlerts && this.renderAlert()}
            </Flex>
        );
    }

    handleFiltersModalSubmit(filtersState) {
        const filters = fromFiltersModalState(filtersState, this.state.filters);

        this.setState({ filters, calendar: filters.calendar }, this.customSubmitFilters);
    }

    handleFiltersModalReset(filtersState) {
        const filters = fromFiltersModalState(filtersState, this.state.filters);

        this.setState({ filters, calendar: filters.calendar }, () => {
            this.submitFilters();
            this.setState((prevState) => ({ activeFiltersKey: prevState.activeFiltersKey + 1 }));
        });
    }

    renderFiltersModal() {
        const { user, toggleMoreFiltersModalDisplay, openMoreFiltersModal, groups } = this.props;

        if (!openMoreFiltersModal) {
            this.filtersModalInitialValue = null;
        } else if (!this.filtersModalInitialValue) {
            this.filtersModalInitialValue = toFiltersModalState(this.state.filters);
        }

        const reverseHomes = toFiltersModalReverseHomes(user?.get('homes') ?? []);
        const collection = {
            isMember: Boolean(user?.get('is_he_collection')),
            isEligible: Boolean(user?.isUserEligibleCollection()),
            learnMoreUrl: i18n.t('url:my-plan.url')
        };

        return (
            <FiltersModal
                isOpen={openMoreFiltersModal}
                onClose={toggleMoreFiltersModalDisplay}
                onSubmit={this.handleFiltersModalSubmit}
                onReset={this.handleFiltersModalReset}
                locale={i18n.language}
                initialValue={this.filtersModalInitialValue ?? undefined}
                groups={toFiltersModalGroups(groups)}
                collection={collection}
                {...(reverseHomes.length > 0 && {
                    reverseSearch: {
                        homes: reverseHomes,
                        jawgToken: process.env.JAWG_KEY,
                        jawgUrl: process.env.URL_JAWG_URL
                    }
                })}
            />
        );
    }

    render() {
        const { user } = this.props;
        let excludeFilters = [...EXCLUDED_FILTERS];

        if (Screen.isMobile()) {
            excludeFilters = excludeFilters.filter(
                (filter) =>
                    !['reverse', 'preferred_destination', 'reciprocal', 'lastMinute', 'luxury'].includes(
                        filter
                    )
            );
        }

        if (user && user.has('is_he_collection') && user.get('is_he_collection')) {
            excludeFilters = excludeFilters.filter((filter) => filter !== 'lastMinute');
        }

        const nbFilters = this.state.committedFilters
            ? this.countFilters(excludeFilters, this.state.committedFilters)
            : 0;
        const isMapView = this.isMobileMapView();

        return (
            <>
                {user &&
                    !user.get('is_he_collection') &&
                    !user.get('subscription') &&
                    !isMapView &&
                    this.state.showBecomeMemberBanner && (
                        <div className="container-become-member">
                            <div className="become-member-wrapper">
                                <Icon size="large" name="CreditCard" />
                                <p
                                    dangerouslySetInnerHTML={{
                                        __html: i18n.t('search:become_member', {
                                            nb: Transfert.getSponsoredTransferValue(
                                                Transfert.MOTIVE_FIRST_SUBSCRIPTION,
                                                user ? user.get('has_godfather') : false
                                            )
                                        })
                                    }}
                                />
                                <Button
                                    onClick={this.handleBecomeMemberBannerClick}
                                    hierarchy="terciary"
                                    size="medium"
                                >
                                    {i18n.t('subscription:subscription_from_search')}
                                </Button>
                            </div>
                            <IconButton
                                hierarchy="ghost"
                                icon="Cross"
                                onClick={this.handleCloseBecomeMemberBanner}
                            />
                        </div>
                    )}
                <div className="search-filters col-xs-12">
                    <form className="form form-filters">
                        <div
                            id="search-filter"
                            className="search-filter-container"
                            ref={(searchFilter) => {
                                this.searchFilter = searchFilter;
                            }}
                        >
                            <div className="container-search-filter-row">
                                {this.renderFiltersButton(nbFilters)}
                                {this.renderFiltersModal()}
                            </div>
                        </div>
                        {nbFilters > 0 && !isMapView && !Screen.isMobile() && (
                            <Suspense fallback={<div>Loading</div>}>
                                <ActiveFilters
                                    key={this.state.activeFiltersKey}
                                    filters={this.state.committedFilters}
                                    excludes={[
                                        'availability',
                                        'bounds',
                                        'home',
                                        'placeId',
                                        'sort',
                                        'duration'
                                    ]}
                                    query={this.state.query}
                                    removeFilter={this.props.onRemoveFilter}
                                    showFilters={this.openMoreFilters}
                                    resetFilters={(e) => this.resetFilters(e, false)}
                                    chunk
                                    refreshFiltersLayout={this.refreshFiltersLayout}
                                    groups={this.props.groups}
                                />
                            </Suspense>
                        )}
                    </form>
                </div>
            </>
        );
    }
}

export default Filters;
